import { FC } from 'react';
import { generatePath } from 'react-router-dom';

import { ComparedField, FieldVerdict, type RankedCandidate } from '@/api/contact-import/types';
import BriefcaseIcon from '@/assets/svg/ui-elements/briefcase.svg';
import BuildingIcon from '@/assets/svg/ui-elements/building.svg';
import GlobeIcon from '@/assets/svg/ui-elements/globe.svg';
import LocationIcon from '@/assets/svg/ui-elements/location.svg';
import MailIcon from '@/assets/svg/ui-elements/mail.svg';
import PhoneIcon from '@/assets/svg/ui-elements/phone.svg';
import ExternalLinkIcon from '@/assets/svg/ui-elements/share-box.svg';
import UserIcon from '@/assets/svg/ui-elements/user-outline.svg';
import { PagePaths } from '@/routes/page-paths';
import { Radio, Spinner, theme, Typography } from '@/shared/design-system';

import {
  CandidateCardContainer,
  CandidateHeaderRow,
  CandidateNameText,
  CandidateVerdictBadge,
  CandidateVerdictList,
  CandidateVerdictRow,
} from './styled';
import { usePreviewContact } from './use-preview-contact';

const FIELD_ICON: Record<string, FC<{ width?: number; height?: number }>> = {
  [ComparedField.Name]: UserIcon,
  [ComparedField.Email]: MailIcon,
  [ComparedField.Phone]: PhoneIcon,
  [ComparedField.Company]: BuildingIcon,
  [ComparedField.JobTitle]: BriefcaseIcon,
  [ComparedField.Website]: GlobeIcon,
  [ComparedField.Address]: LocationIcon,
};

const VERDICT_BADGE: Record<string, { color: 'primary' | 'yellow' | 'secondary' }> = {
  [FieldVerdict.Matches]: { color: 'primary' },
  [FieldVerdict.Contradicts]: { color: 'yellow' },
  [FieldVerdict.Neutral]: { color: 'secondary' },
};

interface CandidateCardProps {
  candidate: RankedCandidate;
  radioValue: string;
}

export const CandidateCard: FC<CandidateCardProps> = ({ candidate, radioValue }) => {
  const { data: contact, isLoading } = usePreviewContact(candidate.contactId);
  const isWeak = candidate.nameConsistency !== 'Consistent';

  return (
    <CandidateCardContainer>
      <CandidateHeaderRow>
        <Radio value={radioValue} size="md" />
        {isLoading ? (
          <Spinner />
        ) : (
          <CandidateNameText
            variant="body2"
            fontWeight={500}
            color={isWeak ? theme.colors.warning.text : theme.colors.brand.text}
          >
            {contact?.name}
          </CandidateNameText>
        )}
        <a
          href={generatePath(PagePaths.addressBookContact, { id: candidate.contactId })}
          target="_blank"
          rel="noreferrer"
          aria-label={
            contact?.name ? `Open ${contact.name} in a new tab` : 'Open contact in a new tab'
          }
          style={{ marginLeft: 'auto', display: 'flex', flexShrink: 0 }}
        >
          <ExternalLinkIcon width={16} height={16} />
        </a>
      </CandidateHeaderRow>

      <CandidateVerdictList>
        {candidate.fieldVerdicts.map((verdict, index) => {
          const Icon = FIELD_ICON[verdict.field] ?? UserIcon;
          const badge = VERDICT_BADGE[verdict.verdict];
          return (
            <CandidateVerdictRow key={`${verdict.field}-${index}`}>
              <Icon width={16} height={16} />
              <Typography variant="body3" color={theme.colors.text.secondary} style={{ flex: 1 }}>
                {verdict.field}
              </Typography>
              {verdict.value && (
                <CandidateVerdictBadge
                  label={verdict.value}
                  size="sm"
                  color={badge?.color ?? 'secondary'}
                  variant="subtle"
                />
              )}
            </CandidateVerdictRow>
          );
        })}
      </CandidateVerdictList>
    </CandidateCardContainer>
  );
};
